import { Button } from '@components/Common/Button';
import React, { Component, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';

interface ComponentProps {
  header: string;
  close: () => void;
  children?: ReactNode;
}

type Props = ComponentProps & WithTranslation;

const modalRoot = document.getElementById('modal-root');

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba(33, 33, 33, 0.85);
  z-index: 1000;
`;

const ModalDialog = styled.div`
  min-width: 700px;
  max-width: 800px;
  position: fixed;
  top: 20%;
  left: 30%;
  right: 30%;
  background-color: rgba(250, 250, 250, 1);
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.75);
  z-index: 1001;
`;

const Header = styled.header`
  background: rgb(51, 63, 72);
  color: rgba(255, 255, 255, 0.45);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.7rem;
  padding: 0.2rem 2rem;

  span {
    padding: 5px 0;
  }
`;

const CloseLink = styled.a`
  font-size: 1.2rem;
  color: white;
  &:hover {
    color: rgba(255, 255, 255, 0.65);
  }
`;

const ContentContainer = styled.div`
  padding: 15px;
`;

export class Modal extends Component<Props> {
  public el: HTMLDivElement;

  constructor(props) {
    super(props);
    this.el = document.createElement('div');
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  public componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    modalRoot.appendChild(this.el);
    document.body.addEventListener('keydown', this.onKeyDown, false);
  }

  public componentWillUnmount() {
    document.body.removeEventListener('keydown', this.onKeyDown, false);
    modalRoot.removeChild(this.el);
  }

  public render() {
    const { header } = this.props;

    return ReactDOM.createPortal(
      <Background>
        <ModalDialog role="dialog">
          <Header>
            <span>{header}</span>
            <CloseLink onClick={this.props.close} aria-label="close">
              X
            </CloseLink>
          </Header>
          <ContentContainer>{this.props.children}</ContentContainer>
        </ModalDialog>
      </Background>,
      this.el
    );
  }

  private onKeyDown(e: KeyboardEvent) {
    const isEscape = n => n === 27;

    if (isEscape(e.keyCode)) {
      e.preventDefault();
      e.stopPropagation();
      this.props.close();
    }
  }
}

export default withTranslation()(Modal) as React.ComponentType<ComponentProps>;
